import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { Shield, Users, Copy, Check, RefreshCw, Eye, Plus, Trash2, Mail, Clock, Gamepad2, Calendar, Edit2, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { adminAPI, rosterAPI, feedbackAPI, invitationsAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { format, parseISO, formatDistanceToNow } from 'date-fns';


// All Users Table Component
const AllUsersTable = () => {
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const limit = 10;

  useEffect(() => {
    if (!searchTerm.trim()) {
      setUsers([]);
      setTotalUsers(0);
      setLoading(false);
      return;
    }

    const delayDebounceFn = setTimeout(() => {
      fetchUsers(page, searchTerm);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, page]);

  const fetchUsers = async (currentPage, search) => {
    try {
      setLoading(true);
      const skip = (currentPage - 1) * limit;
      const response = await adminAPI.getAllUsers({ skip, limit, search });

      // Backend returns { users: [], total: 100, skip: 0, limit: 10 }
      // Or fallback to old array format if API hasn't updated immediately (though we updated it)
      if (response.data.users) {
        setUsers(response.data.users);
        setTotalUsers(response.data.total);
        setTotalPages(Math.ceil(response.data.total / limit));
      } else {
        // Fallback for old API response (array)
        setUsers(response.data);
        setTotalUsers(response.data.length);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(1); // Reset to first page on search
  };

  const formatLastLogin = (lastLogin) => {
    if (!lastLogin) return 'Never';
    try {
      return formatDistanceToNow(new Date(lastLogin), { addSuffix: true });
    } catch {
      return 'Unknown';
    }
  };

  const getRoleBadgeColor = (role) => {
    const normalizedRole = role.toUpperCase();
    switch (normalizedRole) {
      case 'ADMIN':
        return 'bg-red-600';
      case 'COACH':
        return 'bg-blue-600';
      case 'PLAYER':
        return 'bg-green-600';
      default:
        return 'bg-gray-600';
    }
  };

  return (
    <div>
      {/* Search and Stats */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
        <Input
          type="text"
          placeholder="Search users..."
          value={searchTerm}
          onChange={handleSearch}
          className="bg-[#0a0a0b] border-gray-700 text-white max-w-md"
        />
        <div className="text-gray-400 text-sm">
          Total Users: <span className="text-white font-bold">{totalUsers}</span>
        </div>
      </div>

      {/* Users View */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : !searchTerm.trim() ? (
        <div className="flex flex-col items-center justify-center py-12 text-center space-y-4">
          <div className="w-16 h-16 bg-blue-600/10 rounded-full flex items-center justify-center text-blue-500">
            <Search className="w-8 h-8" />
          </div>
          <div>
            <h3 className="text-white font-medium">Search for a user</h3>
            <p className="text-sm text-gray-500 max-w-xs mx-auto">
              Type a username above to find and manage a specific account.
            </p>
          </div>
        </div>
      ) : users.length === 0 ? (
        <p className="text-center text-gray-500 py-8">No users found for "{searchTerm}"</p>
      ) : (
        <>
          {/* Mobile-only Card View */}
          <div className="md:hidden space-y-4" data-testid="user-cards-mobile">
            {users.map((user) => (
              <Card key={user.id} className="bg-[#111112] border-gray-800 hover:border-gray-700 transition-colors shadow-sm">
                <CardContent className="p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-600/20 border border-blue-600/30 flex items-center justify-center text-blue-400 font-bold shrink-0">
                        {user.username.substring(0, 2).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="text-white font-semibold truncate max-w-[150px]">{user.username}</div>
                        <div className="text-[10px] text-gray-500 truncate max-w-[150px]">{user.email}</div>
                      </div>
                    </div>
                    <Badge className={`${getRoleBadgeColor(user.role)} scale-90`}>
                      {user.role}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-2 gap-4 py-3 border-y border-gray-800/50">
                    <div className="space-y-1">
                      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-medium">Team</div>
                      <div className="text-sm text-gray-300 truncate">
                        {user.team_name || <span className="text-gray-600 italic">No team</span>}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-medium">Joined</div>
                      <div className="text-sm text-gray-300">
                        {format(parseISO(user.created_at), 'MMM d, yyyy')}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3 pt-1">
                    <div className="flex flex-col gap-2">
                      <label className="text-[10px] uppercase tracking-wider text-gray-500 font-medium">Membership & Status</label>
                      <div className="flex items-center gap-3">
                        <select
                          value={user.membership_type || 'standard'}
                          onChange={async (e) => {
                            try {
                              await adminAPI.updateUserMembershipType(user.id, e.target.value);
                              toast({ title: 'Updated', description: `Membership → ${e.target.value.toUpperCase()}` });
                              fetchUsers(page, searchTerm);
                            } catch (err) {
                              toast({ variant: 'destructive', title: 'Error', description: err.response?.data?.detail || 'Failed' });
                            }
                          }}
                          className={`bg-[#0a0a0b] border border-gray-700 text-xs rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-500 font-medium flex-1 ${(user.membership_type || 'standard') === 'seller' ? 'text-emerald-400' : 'text-gray-400'
                            }`}
                        >
                          <option value="standard">Standard</option>
                          <option value="seller">Seller</option>
                        </select>
                        {(user.membership_type || 'standard') === 'seller' && (
                          <button
                            onClick={async () => {
                              try {
                                const newStatus = !user.is_partner;
                                await adminAPI.updateUserPartnerStatus(user.id, newStatus);
                                toast({ title: 'Updated', description: `Partner status ${newStatus ? 'enabled' : 'disabled'}` });
                                fetchUsers(page, searchTerm);
                              } catch (err) {
                                toast({ variant: 'destructive', title: 'Error', description: err.response?.data?.detail || 'Failed' });
                              }
                            }}
                            className={`flex-shrink-0 p-1 rounded-full transition-colors ${user.is_partner ? 'text-blue-400 bg-blue-400/10' : 'text-gray-600 bg-gray-600/10 hover:text-gray-400'}`}
                            title="Toggle Partner Status"
                          >
                            <svg className="w-6 h-6" viewBox="0 0 24 24" fill={user.is_partner ? "currentColor" : "none"} stroke={user.is_partner ? "none" : "currentColor"}>
                              <circle cx="12" cy="12" r="10" />
                              <path d="M9 12l2 2 4-4" stroke={user.is_partner ? "white" : "currentColor"} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col gap-2">
                      <label className="text-[10px] uppercase tracking-wider text-gray-500 font-medium">Market Limit (active: {user.active_listing_count || 0})</label>
                      <input
                        type="number"
                        min="0"
                        defaultValue={user.marketplace_listing_limit_override ?? ''}
                        placeholder={String(user.marketplace_listing_limit || 5)}
                        onBlur={async (e) => {
                          const val = e.target.value.trim();
                          const newLimit = val === '' ? null : parseInt(val);
                          if (newLimit === user.marketplace_listing_limit_override) return;
                          try {
                            await adminAPI.updateUserMarketplaceLimit(user.id, newLimit);
                            toast({ title: 'Updated', description: newLimit !== null ? `Limit → ${newLimit}` : 'Override cleared' });
                            fetchUsers(page, searchTerm);
                          } catch (err) {
                            toast({ variant: 'destructive', title: 'Error', description: 'Failed to update limit' });
                          }
                        }}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                        className="w-full bg-[#0a0a0b] border border-gray-700 text-white text-xs rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-2 pt-2 text-[10px] text-gray-500">
                    <Clock className="h-3 w-3" />
                    <span>Last seen: {user.last_login ? formatLastLogin(user.last_login) : <span className="italic">Never</span>}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Desktop-only Table View */}
          <div className="hidden md:block overflow-x-auto scrollbar-hide">
            <Table>
              <TableHeader>
                <TableRow className="border-gray-800">
                  <TableHead className="text-gray-400 whitespace-nowrap">Username</TableHead>
                  <TableHead className="text-gray-400 hidden lg:table-cell">Email</TableHead>
                  <TableHead className="text-gray-400">Role</TableHead>
                  <TableHead className="text-gray-400">Team</TableHead>
                  <TableHead className="text-gray-400">Membership</TableHead>
                  <TableHead className="text-gray-400">Market Limit</TableHead>
                  <TableHead className="text-gray-400">Last Login</TableHead>
                  <TableHead className="text-gray-400 hidden xl:table-cell">Joined</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} className="border-gray-800">
                    <TableCell className="font-medium text-white whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-medium shrink-0">
                          {user.username.substring(0, 2).toUpperCase()}
                        </div>
                        {user.username}
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-400 text-sm hidden lg:table-cell whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Mail className="h-3 w-3 text-gray-500" />
                        {user.email}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getRoleBadgeColor(user.role)}>
                        {user.role}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-gray-300 whitespace-nowrap">
                      {user.team_name || <span className="text-gray-500 italic">No team</span>}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <select
                          value={user.membership_type || 'standard'}
                          onChange={async (e) => {
                            try {
                              await adminAPI.updateUserMembershipType(user.id, e.target.value);
                              toast({ title: 'Updated', description: `Membership → ${e.target.value.toUpperCase()}` });
                              fetchUsers(page, searchTerm);
                            } catch (err) {
                              toast({ variant: 'destructive', title: 'Error', description: err.response?.data?.detail || 'Failed' });
                            }
                          }}
                          className={`bg-[#0a0a0b] border border-gray-700 text-sm rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-emerald-500 font-medium ${(user.membership_type || 'standard') === 'seller' ? 'text-emerald-400' : 'text-gray-400'
                            }`}
                        >
                          <option value="standard">Standard</option>
                          <option value="seller">Seller</option>
                        </select>
                        {(user.membership_type || 'standard') === 'seller' && (
                          <button
                            onClick={async () => {
                              try {
                                const newStatus = !user.is_partner;
                                await adminAPI.updateUserPartnerStatus(user.id, newStatus);
                                toast({ title: 'Updated', description: `Partner status ${newStatus ? 'enabled' : 'disabled'}` });
                                fetchUsers(page, searchTerm);
                              } catch (err) {
                                toast({ variant: 'destructive', title: 'Error', description: err.response?.data?.detail || 'Failed' });
                              }
                            }}
                            className={`flex-shrink-0 transition-colors ${user.is_partner ? 'text-[#1DA1F2] hover:opacity-80' : 'text-gray-600 hover:text-gray-400'}`}
                            title="Toggle Partner Status"
                          >
                            <svg className="w-5 h-5" viewBox="0 0 24 24" fill={user.is_partner ? "currentColor" : "none"} stroke={user.is_partner ? "none" : "currentColor"}>
                              <circle cx="12" cy="12" r="10" />
                              <path d="M9 12l2 2 4-4" stroke={user.is_partner ? "white" : "currentColor"} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="0"
                          defaultValue={user.marketplace_listing_limit_override ?? ''}
                          placeholder={String(user.marketplace_listing_limit || 5)}
                          onBlur={async (e) => {
                            const val = e.target.value.trim();
                            const newLimit = val === '' ? null : parseInt(val);
                            if (newLimit === user.marketplace_listing_limit_override) return;
                            try {
                              await adminAPI.updateUserMarketplaceLimit(user.id, newLimit);
                              toast({ title: 'Updated', description: newLimit !== null ? `Limit → ${newLimit}` : 'Override cleared' });
                              fetchUsers(page, searchTerm);
                            } catch (err) {
                              toast({ variant: 'destructive', title: 'Error', description: 'Failed to update limit' });
                            }
                          }}
                          onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                          className="w-16 bg-[#0a0a0b] border border-gray-700 text-white text-sm rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                        />
                        <span className="text-[10px] text-gray-600">
                          {user.active_listing_count || 0} active
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-400 text-sm whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Clock className="h-3 w-3 text-gray-500" />
                        <span className={user.last_login ? 'text-white' : 'text-gray-500 italic'}>
                          {formatLastLogin(user.last_login)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-400 text-sm hidden xl:table-cell whitespace-nowrap">
                      {format(parseISO(user.created_at), 'MMM d, yyyy')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4 border-t border-gray-800 pt-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={page === 1}
          className="text-gray-400 border-gray-700 hover:bg-gray-800"
        >
          Previous
        </Button>
        <span className="text-sm text-gray-400">
          Page {page} of {totalPages || 1}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPage(p => Math.min(totalPages, p + 1))}
          disabled={page >= totalPages}
          className="text-gray-400 border-gray-700 hover:bg-gray-800"
        >
          Next
        </Button>
      </div>
    </div>
  );
};

const EventManagement = () => {
  const [events, setEvents] = useState([]);
  const [activeEvent, setActiveEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  // Form state
  const [scheduleForm, setScheduleForm] = useState({
    name: '',
    startDate: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
    endDate: format(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), "yyyy-MM-dd'T'HH:mm"),
    type: 'WEEKLY_HIGHLIGHTS'
  });

  useEffect(() => {
    fetchAllEvents();
  }, []);

  const fetchAllEvents = async () => {
    try {
      setLoading(true);
      const [allResponse, activeResponse] = await Promise.all([
        adminAPI.getAllEvents(),
        adminAPI.getActiveEvent()
      ]);
      setEvents(allResponse.data);
      setActiveEvent(activeResponse.data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleEvent = async (e) => {
    e.preventDefault();
    try {
      setProcessing(true);
      await adminAPI.startEvent(scheduleForm.type, 7, {
        start_date: scheduleForm.startDate,
        end_date: scheduleForm.endDate,
        name: scheduleForm.name
      });
      setShowScheduleModal(false);
      await fetchAllEvents();
    } catch (error) {
      console.error('Failed to schedule event:', error);
      alert(error.response?.data?.detail || 'Failed to schedule event');
    } finally {
      setProcessing(false);
    }
  };

  const handleEndEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to end this event?')) return;

    try {
      setProcessing(true);
      await adminAPI.endEvent(eventId);
      await fetchAllEvents();
    } catch (error) {
      console.error('Failed to end event:', error);
      alert(error.response?.data?.detail || 'Failed to end event');
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelEvent = async (eventId) => {
    if (!window.confirm('Bu eventi iptal etmek istediğinize emin misiniz?')) return;

    try {
      setProcessing(true);
      await adminAPI.cancelEvent(eventId);
      await fetchAllEvents();
    } catch (error) {
      console.error('Failed to cancel event:', error);
      alert(error.response?.data?.detail || 'Failed to cancel event');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) return null;

  return (
    <div className="space-y-6 mb-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <Gamepad2 className="w-6 h-6 text-purple-500" />
          Event Management
        </h2>
        <Button
          onClick={() => setShowScheduleModal(true)}
          className="bg-purple-600 hover:bg-purple-700 h-9 w-full sm:w-auto"
        >
          <Plus className="w-4 h-4 mr-2" />
          Schedule Event
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Active Event Card */}
        <Card className="bg-[#1a1a1b] border-purple-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-400 uppercase tracking-wider flex items-center justify-between">
              Active Event
              {activeEvent && <Badge className="bg-green-600 animate-pulse">LIVE</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {activeEvent ? (
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-bold text-white">{activeEvent.name || activeEvent.type}</h3>
                  <p className="text-sm text-gray-400 mt-1">
                    Ends {format(parseISO(activeEvent.end_date), 'MMM d, HH:mm')}
                  </p>
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleEndEvent(activeEvent.id)}
                  disabled={processing}
                  className="w-full bg-red-600/20 hover:bg-red-600/40 text-red-500 border border-red-600/50"
                >
                  End Session Now
                </Button>
              </div>
            ) : (
              <div className="py-4 text-center">
                <p className="text-gray-500 text-sm">No event currently active</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Stats or Info */}
        <Card className="bg-[#1a1a1b] border-gray-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-400 uppercase tracking-wider">
              Upcoming Events
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {events.filter(e => e.status === 'scheduled').slice(0, 3).map(event => (
                <div key={event.id} className="flex items-center justify-between p-2 rounded bg-[#0a0a0b] border border-gray-800">
                  <div>
                    <p className="text-xs font-medium text-white truncate w-32">{event.name || event.type}</p>
                    <p className="text-[10px] text-gray-500">{format(parseISO(event.start_date), 'MMM d, HH:mm')}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px] h-5 border-purple-500/50 text-purple-400">
                      Scheduled
                    </Badge>
                    <button
                      onClick={() => handleCancelEvent(event.id)}
                      disabled={processing}
                      className="text-red-400 hover:text-red-300 text-xs px-1"
                      title="İptal Et"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
              {events.filter(e => e.status === 'scheduled').length === 0 && (
                <p className="text-xs text-gray-600 italic py-4 text-center">No upcoming events planned</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Full Events Table */}
      <Card className="bg-[#1a1a1b] border-gray-800 mt-4">
        <CardHeader className="pb-2 border-b border-gray-800">
          <CardTitle className="text-white text-sm">Event History & Logs</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto scrollbar-hide">
            <Table>
              <TableHeader>
                <TableRow className="border-gray-800 hover:bg-transparent">
                  <TableHead className="text-[11px] uppercase text-gray-500">Event</TableHead>
                  <TableHead className="text-[11px] uppercase text-gray-500">Status</TableHead>
                  <TableHead className="text-[11px] uppercase text-gray-500 hidden md:table-cell">Start Date</TableHead>
                  <TableHead className="text-[11px] uppercase text-gray-500 hidden lg:table-cell">End Date</TableHead>
                  <TableHead className="text-[11px] uppercase text-gray-500">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.slice(0, 5).map((event) => (
                  <TableRow key={event.id} className="border-gray-800">
                    <TableCell className="text-sm font-medium text-white">
                      {event.name || event.type}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={`text-[10px] ${event.status === 'active' ? 'bg-green-600/20 text-green-400' :
                        event.status === 'scheduled' ? 'bg-purple-600/20 text-purple-400' :
                          event.status === 'cancelled' ? 'bg-red-600/20 text-red-400' :
                            'bg-gray-800 text-gray-400'
                        }`}>
                        {event.status.toUpperCase()}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-gray-400 hidden md:table-cell">
                      {format(parseISO(event.start_date), 'MMM d, yyyy HH:mm')}
                    </TableCell>
                    <TableCell className="text-xs text-gray-400 hidden lg:table-cell">
                      {format(parseISO(event.end_date), 'MMM d, yyyy HH:mm')}
                    </TableCell>
                    <TableCell>
                      {(event.status === 'scheduled' || event.status === 'active') && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCancelEvent(event.id)}
                          disabled={processing}
                          className="h-7 px-2 text-red-400 hover:text-red-300 hover:bg-red-600/10"
                        >
                          İptal Et
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Schedule Modal */}
      <Dialog open={showScheduleModal} onOpenChange={setShowScheduleModal}>
        <DialogContent className="bg-[#1a1a1b] border-gray-800 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Schedule New Event</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleScheduleEvent} className="space-y-4 pt-4">
            <div className="space-y-2">
              <label className="text-xs font-medium text-gray-400 uppercase tracking-wider">Event Name</label>
              <Input
                value={scheduleForm.name}
                onChange={e => setScheduleForm({ ...scheduleForm, name: e.target.value })}
                placeholder="e.g. Winter Highlights 2024"
                className="bg-[#0a0a0b] border-gray-700 text-white"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-xs font-medium text-gray-400 uppercase tracking-wider">Start Date</label>
                <Input
                  type="datetime-local"
                  value={scheduleForm.startDate}
                  onChange={e => setScheduleForm({ ...scheduleForm, startDate: e.target.value })}
                  className="bg-[#0a0a0b] border-gray-700 text-white"
                />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-medium text-gray-400 uppercase tracking-wider">End Date</label>
                <Input
                  type="datetime-local"
                  value={scheduleForm.endDate}
                  onChange={e => setScheduleForm({ ...scheduleForm, endDate: e.target.value })}
                  className="bg-[#0a0a0b] border-gray-700 text-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-medium text-gray-400 uppercase tracking-wider">Event Type</label>
              <select
                value={scheduleForm.type}
                onChange={e => setScheduleForm({ ...scheduleForm, type: e.target.value })}
                className="w-full bg-[#0a0a0b] border border-gray-700 text-white rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="WEEKLY_HIGHLIGHTS">Weekly Highlights</option>
                <option value="TOURNAMENT">Tournament</option>
                <option value="CUSTOM">Custom Event</option>
              </select>
            </div>

            <DialogFooter className="mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowScheduleModal(false)}
                className="border-gray-700 text-gray-400 hover:bg-gray-800"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-purple-600 hover:bg-purple-700"
                disabled={processing}
              >
                {processing ? 'Processing...' : 'Schedule Event'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const AdminPanel = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamLogoUrl, setNewTeamLogoUrl] = useState('');
  const [creating, setCreating] = useState(false);
  const [teamStorage, setTeamStorage] = useState({});
  const [editLimitDialog, setEditLimitDialog] = useState({ open: false, teamId: null, limit: 5 });

  const handleEditLimit = (team) => {
    setEditLimitDialog({
      open: true,
      teamId: team.id,
      limit: team.storage_limit_gb || 5
    });
  };

  const handleSaveLimit = async () => {
    try {
      await adminAPI.updateTeamStorageLimit(editLimitDialog.teamId, parseInt(editLimitDialog.limit));
      setEditLimitDialog(prev => ({ ...prev, open: false }));
      await loadTeams(); // Reload to see updates
    } catch (error) {
      console.error('Failed to update limit:', error);
      alert('Failed to update storage limit');
    }
  };

  const handlePlanChange = async (teamId, newPlan) => {
    try {
      await adminAPI.updateTeamPlan(teamId, newPlan);
      toast({
        title: "Plan Updated",
        description: `Team plan changed to ${newPlan.toUpperCase()}`
      });
      await loadTeams(); // Reload to see updated plan and storage limit
    } catch (error) {
      console.error('Failed to update plan:', error);
      toast({
        title: "Update Failed",
        description: error.response?.data?.detail || "Failed to update team plan",
        variant: "destructive"
      });
    }
  };

  // Check admin access
  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    loadTeams();
  }, []);

  const loadTeams = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getTeams();
      setTeams(response.data.teams || []);

      // Fetch storage separately - won't break app if it fails
      try {
        const storageResponse = await adminAPI.getTeamStorage();
        const storageMap = {};
        (storageResponse.data.teams || []).forEach(team => {
          storageMap[team.team_id] = team;
        });
        setTeamStorage(storageMap);
      } catch (storageError) {
        console.warn('Could not load storage stats:', storageError);
        // App continues normally
      }
    } catch (error) {
      console.error('Failed to load teams:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewTeam = async (team) => {
    setSelectedTeam(team);
    setShowMembersModal(true);
    setMembersLoading(true);

    try {
      const response = await rosterAPI.getRoster(team.id);
      setTeamMembers(response.data);
    } catch (error) {
      console.error('Failed to load team members:', error);
      setTeamMembers([]);
    } finally {
      setMembersLoading(false);
    }
  };

  const handleCopyCode = async (teamId, code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(teamId);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const handleRegenerateCode = async (teamId) => {
    if (!window.confirm('Are you sure you want to regenerate this team code? The old code will stop working.')) {
      return;
    }

    try {
      await adminAPI.regenerateCode(teamId);
      await loadTeams();
    } catch (error) {
      console.error('Failed to regenerate code:', error);
      alert(error.response?.data?.detail || 'Failed to regenerate code');
    }
  };

  const handleGenerateInvitation = async (teamId) => {
    try {
      await invitationsAPI.generateCoachCode(teamId);
      await loadTeams();
    } catch (error) {
      console.error('Failed to generate invitation:', error);
      alert(error.response?.data?.detail || 'Failed to generate invitation');
    }
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    if (!newTeamName.trim()) return;

    try {
      setCreating(true);
      await adminAPI.createTeam({
        name: newTeamName.trim(),
        logo_url: newTeamLogoUrl.trim() || null
      });
      setNewTeamName('');
      setNewTeamLogoUrl('');
      setShowCreateModal(false);
      await loadTeams();
    } catch (error) {
      console.error('Failed to create team:', error);
      alert(error.response?.data?.detail || 'Failed to create team');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteTeam = async (teamId, teamName) => {
    if (!window.confirm(`Are you sure you want to delete "${teamName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await adminAPI.deleteTeam(teamId);
      await loadTeams();
    } catch (error) {
      console.error('Failed to delete team:', error);
      alert(error.response?.data?.detail || 'Failed to delete team');
    }
  };

  const maskCode = (code) => {
    if (!code) return '••••••••';
    return code.substring(0, 5) + '•••••';
  };

  const formatStorage = (teamId) => {
    const storage = teamStorage[teamId];
    if (!storage) return '0 MB';

    const mb = storage.total_mb;
    if (mb >= 1024) {
      return `${(mb / 1024).toFixed(2)} GB`;
    }
    return `${mb.toFixed(2)} MB`;
  };

  if (user?.role !== 'admin') {
    return null;
  }

  return (
    <div className="p-4 sm:p-6 md:p-8" data-testid="admin-panel">
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <Shield className="w-6 h-6 sm:w-8 sm:h-8 text-red-500" />
            Admin Panel
          </h1>
          <p className="text-sm text-gray-400">Manage teams, users, and generate invitation codes</p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto"
          data-testid="create-team-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Team
        </Button>
      </div>

      {/* Event Management */}
      <EventManagement />

      {/* Teams List */}
      <Card className="bg-[#1a1a1b] border-gray-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Users className="w-5 h-5" />
            All Teams ({teams.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : teams.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No teams created yet</p>
          ) : (
            <div className="space-y-4">
              {/* Mobile-only Card View */}
              <div className="md:hidden space-y-4" data-testid="team-cards-mobile">
                {teams.map((team) => (
                  <Card key={team.id} className="bg-[#111112] border-gray-800 hover:border-gray-700 transition-colors shadow-sm">
                    <CardContent className="p-4 space-y-4">
                      {/* Header: Logo, Name */}
                      <div className="flex items-center justify-between">
                        <Link to={`/teams/${team.id}/profile`} className="flex items-center gap-3 hover:opacity-80 transition-opacity min-w-0">
                          {team.logo_url ? (
                            <img src={team.logo_url} alt={team.name} className="w-10 h-10 rounded object-cover shrink-0" />
                          ) : (
                            <div className="w-10 h-10 rounded bg-gray-700 flex items-center justify-center text-gray-400 text-xs font-bold shrink-0">
                              {team.name.substring(0, 2).toUpperCase()}
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="text-white font-semibold truncate max-w-[180px]">{team.name}</div>
                            <div className="text-[10px] text-gray-500">Created: {format(parseISO(team.created_at), 'MMM d, yyyy')}</div>
                          </div>
                        </Link>
                      </div>

                      {/* Members & Storage Info Grid */}
                      <div className="grid grid-cols-2 gap-4 py-3 border-y border-gray-800/50">
                        <div className="space-y-1">
                          <div className="text-[10px] uppercase tracking-wider text-gray-500 font-medium">Members</div>
                          <div className="text-sm text-gray-300">
                            <span className="font-bold">{team.total_members}</span>
                            <span className="text-[10px] text-gray-500 ml-1">({team.coach_count}C, {team.player_count}P)</span>
                          </div>
                        </div>
                        <div className="space-y-1">
                          <div className="text-[10px] uppercase tracking-wider text-gray-500 font-medium">Storage</div>
                          <div className="flex items-center gap-1.5 text-sm text-gray-300">
                            <span>{formatStorage(team.id)}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-5 w-5 p-0 text-gray-500 hover:text-white"
                              onClick={() => handleEditLimit(team)}
                            >
                              <Edit2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Plan & Invitation Actions */}
                      <div className="space-y-3 pt-1">
                        <div className="flex flex-col gap-2">
                          <label className="text-[10px] uppercase tracking-wider text-gray-500 font-medium">Plan & Invitation</label>
                          <div className="flex items-center gap-3">
                            <select
                              value={team.plan || 'free'}
                              onChange={(e) => handlePlanChange(team.id, e.target.value)}
                              className={`bg-[#0a0a0b] border border-gray-700 text-xs rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 font-medium flex-1 ${team.plan === 'premium' ? 'text-amber-500' : 'text-gray-400'
                                }`}
                            >
                              <option value="free">Free</option>
                              <option value="premium">Premium</option>
                            </select>

                            {team.latest_coach_invitation ? (
                              <div className="flex items-center gap-1">
                                <code className="bg-[#0a0a0b] px-2 py-1 rounded text-xs text-purple-300 font-mono">
                                  {team.latest_coach_invitation}
                                </code>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleCopyCode(team.id, team.latest_coach_invitation)}
                                  className="text-gray-400 hover:text-white h-8 w-8 p-0"
                                >
                                  {copiedCode === team.id ? (
                                    <Check className="w-4 h-4 text-green-500" />
                                  ) : (
                                    <Copy className="w-4 h-4" />
                                  )}
                                </Button>
                              </div>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleGenerateInvitation(team.id)}
                                className="text-[10px] h-7 px-2 border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                              >
                                <Plus className="w-3 h-3 mr-1" />
                                Generate
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Footer Actions */}
                      <div className="pt-2 flex items-center justify-between gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewTeam(team)}
                          className="text-gray-400 hover:text-white flex-1 h-8 bg-gray-800/50"
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          View
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRegenerateCode(team.id)}
                          className="text-gray-400 hover:text-blue-400 h-8 w-8 bg-gray-800/50"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteTeam(team.id, team.name)}
                          className="text-gray-400 hover:text-red-400 h-8 w-8 bg-gray-800/50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Desktop-only Table View */}
              <div className="hidden md:block overflow-x-auto scrollbar-hide">
                <Table>
                  <TableHeader>
                    <TableRow className="border-gray-800">
                      <TableHead className="text-gray-400">Team Name</TableHead>
                      <TableHead className="text-gray-400">Members</TableHead>
                      <TableHead className="text-gray-400">Plan</TableHead>
                      <TableHead className="text-gray-400 hidden md:table-cell">Storage</TableHead>
                      <TableHead className="text-gray-400 hidden lg:table-cell">Created</TableHead>
                      <TableHead className="text-gray-400 hidden sm:table-cell">Coach Invitations</TableHead>
                      <TableHead className="text-gray-400 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {teams.map((team) => (
                      <TableRow key={team.id} className="border-gray-800" data-testid={`team-row-${team.id}`}>
                        <TableCell className="font-medium text-white">
                          <div className="flex items-center gap-3">
                            <Link to={`/teams/${team.id}/profile`} className="flex items-center gap-3 hover:opacity-80 transition-opacity">
                              {team.logo_url ? (
                                <img src={team.logo_url} alt={team.name} className="w-8 h-8 rounded object-cover" />
                              ) : (
                                <div className="w-8 h-8 rounded bg-gray-700 flex items-center justify-center text-gray-400 text-xs text-nowrap">
                                  {team.name.substring(0, 2).toUpperCase()}
                                </div>
                              )}
                              <span className="hover:underline hover:text-blue-400 text-white font-medium transition-colors text-nowrap">
                                {team.name}
                              </span>
                            </Link>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-gray-300 text-nowrap">
                            <span>{team.total_members}</span>
                            <span className="text-xs text-gray-500 hidden sm:inline">
                              ({team.coach_count} coach{team.coach_count !== 1 ? 'es' : ''}, {team.player_count} player{team.player_count !== 1 ? 's' : ''})
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <select
                            value={team.plan || 'free'}
                            onChange={(e) => handlePlanChange(team.id, e.target.value)}
                            className={`bg-[#0a0a0b] border border-gray-700 text-sm rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 font-medium ${team.plan === 'premium' ? 'text-amber-500' : 'text-gray-400'
                              }`}
                          >
                            <option value="free">Free</option>
                            <option value="premium">Premium</option>
                          </select>
                        </TableCell>
                        <TableCell className="text-gray-300 text-sm hidden md:table-cell">
                          <div className="flex items-center gap-2 text-nowrap">
                            <span>{formatStorage(team.id)}</span>
                            <span className="text-gray-500 text-xs">/ {team.storage_limit_gb || 5} GB</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-gray-500 hover:text-white"
                              onClick={() => handleEditLimit(team)}
                            >
                              <Edit2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell className="text-gray-400 hidden lg:table-cell whitespace-nowrap">
                          {format(parseISO(team.created_at), 'MMM d, yyyy')}
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          {team.latest_coach_invitation ? (
                            <div className="flex items-center gap-2">
                              <code className="bg-[#0a0a0b] px-2 py-1 rounded text-sm text-purple-300 font-mono">
                                {team.latest_coach_invitation}
                              </code>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleCopyCode(team.id, team.latest_coach_invitation)}
                                className="text-gray-400 hover:text-white h-8 w-8 p-0"
                              >
                                {copiedCode === team.id ? (
                                  <Check className="w-4 h-4 text-green-500" />
                                ) : (
                                  <Copy className="w-4 h-4" />
                                )}
                              </Button>
                            </div>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleGenerateInvitation(team.id)}
                              className="text-xs h-7 border-purple-500/50 text-purple-400 hover:bg-purple-500/10 hover:text-purple-300"
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Generate
                            </Button>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewTeam(team)}
                              className="text-gray-400 hover:text-white"
                              data-testid={`view-team-${team.id}`}
                            >
                              <Eye className="w-4 h-4 mr-1 sm:mr-0" />
                              <span className="hidden sm:inline">View</span>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRegenerateCode(team.id)}
                              className="text-gray-400 hover:text-blue-400"
                              data-testid={`regenerate-code-${team.id}`}
                            >
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteTeam(team.id, team.name)}
                              className="text-gray-400 hover:text-red-400"
                              data-testid={`delete-team-${team.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>



      {/* All Users Section */}
      <Card className="bg-[#1a1a1b] border-gray-800 mt-6">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Users className="w-5 h-5" />
            All Users
          </CardTitle>
        </CardHeader>
        <CardContent>
          <AllUsersTable />
        </CardContent>
      </Card>

      {/* Feedback Section */}
      <Card className="bg-[#1a1a1b] border-gray-800 mt-6">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Feedback
          </CardTitle>
        </CardHeader>
        <CardContent>
          <FeedbackTable />
        </CardContent>
      </Card>

      {/* Team Members Modal */}
      <Dialog open={showMembersModal} onOpenChange={setShowMembersModal}>
        <DialogContent className="bg-[#1a1a1b] border-gray-800 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              {selectedTeam?.name} - Members
            </DialogTitle>
          </DialogHeader>
          {membersLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : teamMembers.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No members in this team</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-gray-800">
                  <TableHead className="text-gray-400">Username</TableHead>
                  <TableHead className="text-gray-400">Email</TableHead>
                  <TableHead className="text-gray-400">Role</TableHead>
                  <TableHead className="text-gray-400">Delegated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {teamMembers.map((member) => (
                  <TableRow key={member.id} className="border-gray-800">
                    <TableCell className="font-medium text-white">{member.username}</TableCell>
                    <TableCell className="text-gray-400">{member.email || '-'}</TableCell>
                    <TableCell>
                      <Badge className={member.role === 'coach' ? 'bg-blue-600' : 'bg-gray-600'}>
                        {member.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {member.is_delegate && (
                        <Badge className="bg-purple-600">Coach Access</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Team Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-[#1a1a1b] border-gray-800 text-white">
          <DialogHeader>
            <DialogTitle>Create New Team</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateTeam} className="space-y-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Team Name</label>
              <Input
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
                placeholder="Enter team name"
                required
                className="bg-[#0a0a0b] border-gray-700 text-white"
                data-testid="new-team-name-input"
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Logo URL (optional)</label>
              <Input
                value={newTeamLogoUrl}
                onChange={(e) => setNewTeamLogoUrl(e.target.value)}
                placeholder="https://example.com/logo.png"
                type="url"
                className="bg-[#0a0a0b] border-gray-700 text-white"
              />
              <p className="text-xs text-gray-500 mt-1">Provide a direct URL to the team logo image</p>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                className="border-gray-700 text-gray-300 hover:bg-gray-800"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700"
                disabled={creating}
                data-testid="submit-create-team"
              >
                {creating ? 'Creating...' : 'Create Team'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Storage Limit Modal */}
      <Dialog open={editLimitDialog.open} onOpenChange={(open) => setEditLimitDialog(prev => ({ ...prev, open }))}>
        <DialogContent className="bg-[#1a1a1b] border-gray-800 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Update Storage Limit</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <label className="text-sm text-gray-400">Storage Limit (GB)</label>
              <Input
                type="number"
                min="1"
                value={editLimitDialog.limit}
                onChange={(e) => setEditLimitDialog(prev => ({ ...prev, limit: e.target.value }))}
                className="bg-[#0a0a0b] border-gray-700 text-white"
              />
              <p className="text-xs text-gray-500">
                Set the maximum storage space allowed for this team.
              </p>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setEditLimitDialog(prev => ({ ...prev, open: false }))}
                className="border-gray-700 text-gray-300 hover:bg-gray-800"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveLimit}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Save Limit
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const FeedbackTable = () => {
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchFeedback();
  }, []);

  const fetchFeedback = async () => {
    try {
      const response = await feedbackAPI.getAllFeedback();
      setFeedback(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const filteredFeedback = feedback.filter(f => filter === 'all' || f.type === filter);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (filteredFeedback.length === 0) {
    return <p className="text-center text-gray-500 py-8">No feedback found</p>;
  }

  return (
    <div>
      <div className="flex justify-end mb-4">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="bg-[#0a0a0b] border border-gray-700 text-white p-2 rounded-md text-sm"
        >
          <option value="all">All Types</option>
          <option value="suggestion">Suggestions</option>
          <option value="complaint">Complaints</option>
        </select>
      </div>
      <div className="overflow-x-auto scrollbar-hide">
        <Table>
          <TableHeader>
            <TableRow className="border-gray-800">
              <TableHead className="text-gray-400 hidden md:table-cell">Date</TableHead>
              <TableHead className="text-gray-400 hidden sm:table-cell">User</TableHead>
              <TableHead className="text-gray-400">Type</TableHead>
              <TableHead className="text-gray-400">Message</TableHead>
              <TableHead className="text-gray-400 hidden lg:table-cell">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredFeedback.map((item) => (
              <TableRow key={item.id} className="border-gray-800">
                <TableCell className="text-gray-300 hidden md:table-cell whitespace-nowrap">
                  {new Date(item.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell className="text-gray-300 font-medium hidden sm:table-cell whitespace-nowrap">
                  {item.user_username || 'Unknown'}
                </TableCell>
                <TableCell>
                  <Badge className={item.type === 'suggestion' ? 'bg-blue-600' : 'bg-red-600'}>
                    {item.type}
                  </Badge>
                </TableCell>
                <TableCell className="text-gray-300 max-w-md truncate" title={item.message}>
                  {item.message}
                </TableCell>
                <TableCell className="text-gray-400 capitalize hidden lg:table-cell">{item.status}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default AdminPanel;
